/**
 * PRD3012_R01(생산실적) 라벨 데이터 빌더
 *
 * 흐름 1) 저장 시 자동 출력
 * - 사용자가 저장 버튼 클릭 → Content 저장 핸들러 진입.
 * - "Lot라벨자동출력"이 Y이면, API 호출 전에 extractUnprintedLabelData(store, deletedLotList) 호출해서
 *   이번에 저장할 LOT 중 출력 대상만 LabelPrintData[] 로 추출해 unprintedLabelData 변수에 담아 둠.
 * - 그 다음 post/patch API 호출 → 저장 성공 시 (삭제가 아닐 때만) printBulkLabel(unprintedLabelData, 'label1') 호출.
 *
 * 흐름 2) 수동 출력
 * - 사용자가 LOT를 선택한 뒤 "라벨 출력" 버튼 클릭.
 * - SearchSection에서 선택 LOT를 selectedLabelDataList(LabelPrintData[])로 만들어 두었으면
 *   LabelPrintButton에 labelDataList로 넘기거나, 별도 버튼에서 printBulkLabel(selectedLabelDataList, 'label1') 호출.
 * - buildLabelData는 "LOT 1건 → LabelPrintData 1건" 변환용으로, extractUnprintedLabelData 내부에서 호출됨.
 */

import { LabelPrintData } from "src/apis/labelApi";
import { formatDate } from "src/utils/string/stringUtil";

/** buildLabelData / extractUnprintedLabelData 호출 시 넘기는 스토어 스냅샷 */
export interface PRD3012_R01StoreData {
    editPRD3012_R01: any;
    PRD3012_R01DetailList: any[];
    PRD3012_R01LotList: any[];
}

/** 수량 포맷 (콤마 + 단위) */
const formatQty = (qty: number | undefined, unit: string): string => {
    if (!qty && qty !== 0) return '';
    return `${qty.toLocaleString()}${unit || 'EA'}`;
};

/**
 * LOT 1건 → LabelPrintData 1건 변환. extractUnprintedLabelData가 각 LOT마다 이 함수를 호출해서 배열을 만든다.
 */
export const PRD3012_R01_buildLabelData = (
    lotNo: string,
    itemCd: string,
    store: PRD3012_R01StoreData
): LabelPrintData => {
    const { editPRD3012_R01, PRD3012_R01LotList } = store;

    const lot = PRD3012_R01LotList.find((l: any) => l.lotNo === lotNo);
    const header = editPRD3012_R01;

    if (!lot) throw new Error(`LOT를 찾을 수 없습니다: ${lotNo}`);
    if (!header) throw new Error('생산실적 정보를 찾을 수 없습니다');

    const transportDate = header.productDt
        ? formatDate(new Date(header.productDt))
        : header.orderDt
            ? formatDate(new Date(header.orderDt))
            : formatDate(new Date());

    return {
        lotNo: lot.lotNo || '',
        itemCd: header.itemCd || lot.itemCd || '',
        itemNm: header.itemNm || lot.itemNm || '',
        itemSpec: header.itemSpec || lot.itemSpec || '',
        transportDate,
        itemAccountNm: (header as any).itemAccountNm || (header as any).inoutMethod || '',
        storageLocation: '-',
        qty: formatQty(lot.lotQty, header.itemUnit || lot.itemUnit || 'EA'),
        customer: header?.customNm ?? '',
        customerItemCd: header?.itemCd || '',
        labelType: '[생산입고]',
    };
};

/**
 * 저장 후 자동 출력할 라벨 데이터만 추출.
 *
 * 호출 시점: Content_PRD3012_R01 저장 핸들러 내부, API 호출 전. "Lot라벨자동출력"이 Y일 때만 호출하고,
 * 반환 배열을 unprintedLabelData로 보관해 두었다가 저장 성공 후 printBulkLabel(unprintedLabelData, 'label1') 호출.
 *
 * @param store - 현재 편집 헤더·디테일·로트 목록 (LOT 번호 할당 반영된 상태 권장)
 * @param deletedLotList - 이번 저장에서 삭제할 LOT의 valueIndex 문자열 배열. 이번에 서버에 안 넘어가는 행은 라벨에서 제외
 * @returns 저장 후 출력할 LOT만 담은 LabelPrintData[] (빈 배열 가능)
 *
 * 포함 조건 (모두 만족하는 LOT만 포함)
 * - deletedLotList에 valueIndex가 없음 (삭제 대상 아님)
 * - lotQty > 0 (수량이 있는 LOT만)
 * - inserted === true (신규/유효 입력 행만)
 * PRD3010는 WMS1020와 달리 printed 플래그로 미출력만 걸러내지 않고, 위 조건만 사용함.
 */
export const PRD3012_R01_extractUnprintedLabelData = (
    store: PRD3012_R01StoreData,
    deletedLotList: string[] = []
): LabelPrintData[] => {
    const { PRD3012_R01LotList } = store;
    const unprintedLabelData: LabelPrintData[] = [];
    const deletedSet = new Set(deletedLotList);

    PRD3012_R01LotList.forEach((lot: any) => {
        const valueIndex = lot.valueIndex?.toString() || '';
        if (
            !deletedSet.has(valueIndex) &&
            lot.lotQty > 0 &&
            lot.inserted === true
        ) {
            try {
                unprintedLabelData.push(
                    PRD3012_R01_buildLabelData(lot.lotNo, lot.itemCd || store.editPRD3012_R01?.itemCd, store)
                );
            } catch (error) {
                console.error(`라벨 데이터 구성 실패: ${lot.lotNo}`, error);
            }
        }
    });

    return unprintedLabelData;
};
